import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  DEFAULT_ERROR_MESSAGE,
  NEW_VIRTUAL_SUPPORT_STAGES,
  PLAN_STATUS_KEY,
  SERVICE_ORDER_TYPES,
  STATUS_CODE,
} from 'app-constants'
import { setPendingConfirmation, setServiceOrderMassive } from 'slices/serviceOrder'
import { setDrawerAlert, setStage } from 'slices/drawer'
import { serviceOrderService, wifiService } from 'services'
import { ContextLoader } from 'components/context-loader'
import {
  AutoSupportInfo,
  ServiceOrderInfo,
  MassiveWarning,
  ReschedulingServiceOrder,
  RescheduleConfirmation,
  SchedulingSuccess,
  CancelSuccess,
  SignalVerify,
  StableSignal,
  UnstableSignal,
  ModemEquipmentList,
  ModemReset,
  ModemAwait,
  ModemCableReconnect,
  ModemAlmostDone,
  ModemFinish,
  SchedulingServiceOrder,
  ScheduleConfirmation,
  SelectAddress,
  RedirectChat,
  InvalidContractSupport,
  SuspendedContractWarning,
  AfterTime,
  SignalWeak,
  PhoneSelection,
} from './components'

function getStage(props) {
  return {
    [NEW_VIRTUAL_SUPPORT_STAGES.INITIAL]: <AutoSupportInfo {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.SERVICE_ORDER]: <ServiceOrderInfo {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.MASSIVE_WARNING]: <MassiveWarning {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.RESCHEDULING_SERVICE_ORDER]: (
      <ReschedulingServiceOrder {...props} />
    ),
    [NEW_VIRTUAL_SUPPORT_STAGES.RESCHEDULE_CONFIRMATION]: <RescheduleConfirmation {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.SCHEDULING_SUCCESS]: <SchedulingSuccess {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.CANCEL_SUCCESS]: <CancelSuccess {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.SIGNAL_VERIFY]: <SignalVerify {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.STABLE_SIGNAL]: <StableSignal {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.UNSTABLE_SIGNAL]: <UnstableSignal {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.MODEM_EQUIPMENT_LIST]: <ModemEquipmentList {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.MODEM_RESET]: <ModemReset {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.MODEM_AWAIT]: <ModemAwait {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.MODEM_CABLE_RECONNECT]: <ModemCableReconnect {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.MODEM_ALMOST_DONE]: <ModemAlmostDone {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.MODEM_FINISH]: <ModemFinish {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.SCHEDULING_SERVICE_ORDER]: <SchedulingServiceOrder {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.SCHEDULE_CONFIRMATION]: <ScheduleConfirmation {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.SELECT_ADDRESS]: <SelectAddress {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.REDIRECT_CHAT]: <RedirectChat {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.AFTER_TIME]: <AfterTime {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.SIGNAL_WEAK]: <SignalWeak {...props} />,
    [NEW_VIRTUAL_SUPPORT_STAGES.PHONE_SELECTION]: <PhoneSelection {...props} />,
  }
}

export function NewVirtualSupport() {
  const [serviceOrder, setServiceOrder] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [bookingSelected, setBookingSelected] = useState(null)
  const [connectionStatusData, setConnectionStatusData] = useState({})
  const [contractAddressSelected, setContractAddressSelected] = useState(null)
  const [contactNote, setContactNote] = useState(null)
  const { stage } = useSelector(({ drawer }) => drawer)
  const {
    selected: { id, contractStatus },
  } = useSelector(({ contract }) => contract)
  const { pendingConfirmation, serviceOrderMassive } = useSelector(state => state.serviceOrder)
  const dispatch = useDispatch()
  const { checkServiceOrderActive } = serviceOrderService()
  const { getConnectionStatusRequest } = wifiService()

  useEffect(() => {
    async function loadConnectionsStatus() {
      try {
        const { data } = await getConnectionStatusRequest({ contractId: id })

        setConnectionStatusData({ ...data, hasError: false })
      } catch (error) {
        console.error(error)
        setConnectionStatusData(oldValue => ({ ...oldValue, hasError: true }))
      }
    }

    async function loadServiceOrder() {
      try {
        const { data } = await checkServiceOrderActive(id, SERVICE_ORDER_TYPES.REPAIR)

        setServiceOrder(data)

        const hasServiceWithPendingConfirmation =
          data && pendingConfirmation?.serviceOrderType === SERVICE_ORDER_TYPES.REPAIR
        if (hasServiceWithPendingConfirmation) {
          dispatch(setPendingConfirmation(null))
          dispatch(setStage(NEW_VIRTUAL_SUPPORT_STAGES.SERVICE_ORDER))
        } else if (serviceOrderMassive) {
          dispatch(setStage(NEW_VIRTUAL_SUPPORT_STAGES.SELECT_ADDRESS))
        }
      } catch (error) {
        const errorStatus = error?.response?.status
        const isControlledErrors =
          errorStatus === STATUS_CODE.NOT_FOUND || errorStatus === STATUS_CODE.BAD_REQUEST

        if (isControlledErrors) console.error(error)
        else {
          dispatch(
            setDrawerAlert({
              severity: 'error',
              title: error?.response?.data?.message || DEFAULT_ERROR_MESSAGE,
            })
          )
        }
      }
    }

    async function loadDrawerData() {
      setIsLoading(true)
      await loadServiceOrder()
      await loadConnectionsStatus()
      setIsLoading(false)
    }

    loadDrawerData()
    return () => dispatch(setServiceOrderMassive(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, pendingConfirmation, serviceOrderMassive])

  if (contractStatus === PLAN_STATUS_KEY.ST_CONT_SUSP_DEBITO) return <SuspendedContractWarning />

  if (contractStatus !== PLAN_STATUS_KEY.ST_CONT_HABILITADO)
    return <InvalidContractSupport contractStatus={contractStatus} />

  if (isLoading) return <ContextLoader fixed={false} />

  return getStage({
    serviceOrder,
    pendingConfirmation,
    setBookingSelected,
    bookingSelected,
    connectionStatusData,
    setConnectionStatusData,
    contractAddressSelected,
    setContractAddressSelected,
    serviceOrderMassive,
    contactNote,
    setContactNote,
  })[stage]
}
